import { IStadium } from '../stadium/stadium';
import { IPicture } from '../image/picture';
import { IEventFoesData } from './event-foes-data';
import { IEventVideos } from './event-videos';
import { IEventTeam } from './event-team';
import { IEventTimestamps } from './event-timestamps';
import {
  AutoPhases,
  EventStatus,
  InGamePhases,
  PostGameAndCancelledPhases,
  PreGamePhases,
} from '@api/models/event/event-status';
import { IVideo } from '@api/models/video/video';
import {
  IStreamingInfo,
  LivestreamStatus,
} from '@api/models/livestream/streaming-info';
import { IEventScores } from '@api/models/event/event-scores';

export class Event {
  _id: string;
  teams: {
    home: IEventTeam;
    away: IEventTeam;
    neutral: IEventTeam;
  };
  spectators: number;
  status: EventStatus;
  timestamps: IEventTimestamps;
  scores: IEventScores;
  delay: boolean;
  leagueid: string;
  leagueType: number;
  stadium: IStadium;
  stateids: string[];
  sex: string;
  referee: { firstname: string; lastname: string };
  extratime_possible: boolean;
  last_goal: number;
  analytics_processed: boolean;
  test_event: boolean;
  round: {
    nr: number;
    ui_string: string;
    ui_name: string;
    group_char?: string;
  };
  userid: string;
  liveticker_status: string;
  _created: number;
  _modified: number;
  short_url: string;
  league_name: string;
  league_pictures: IPicture[];
  videos?: IEventVideos;
  videos_native?: IVideo[];
  foes: IEventFoesData;
  sportradar?: {
    sr_created: boolean;
    sr_verified: boolean;
    sr_match_id: string;
    sr_last_update_ts: number;
    sr_api: string;
  };
  nu?: {
    nu_verified?: boolean;
    nu_meeting_id?: string;
  };
  staige?: {
    staige_event_id?: number;
    staige_camera_id?: number;
  };
  streaming_info?: IStreamingInfo;
  seasonid?: string;
  league_info?: {
    league_name: string;
    league_performance_level: number;
    league_readable_id: string;
    league_type: number;
    video_ticker: string;
  };
  hasReporter = false;
  pictures: IPicture[];
  type?: string;
  video_ticker_verification?: {
    top_actions?: number;
    video_tickered?: boolean;
    score_changes?: number;
  };
  analytics?: {
    view_counts?: {
      total_views?: number;
      livegame_views?: number;
      postgame_views?: number;
      pregame_views?: number;
    };
  };
  liveStreamStatusString?: string;
  liveStreamStatusColor?: string;

  public constructor(init?: Partial<Event>) {
    Object.assign(this, init);
    this.setHasReporter();
    this.setStreamInfo();
  }

  public isSportradar(): boolean {
    if (!this.sportradar) {
      return false;
    }
    return this.sportradar.sr_created && !!this.sportradar.sr_match_id;
  }

  public hasPenalty(): boolean {
    const timestamps = this.timestamps?.overtime;
    const scores = this.scores?.overtime;
    if (!timestamps || !scores) {
      return false;
    }
    return timestamps.shootout?.start && scores.shootout?.end !== '-1:-1';
  }

  public hasOvertime(): boolean {
    const timestamps = this.timestamps?.overtime;
    const scores = this.scores?.overtime;
    if (!timestamps || !scores) {
      return false;
    }
    return timestamps.firsthalf?.start && scores.firsthalf?.end !== '-1:-1';
  }

  public willStartSoon(): boolean {
    // Game will start in 30 minutes or less
    if (this.isPreGame()) {
      return this.timestamps.start - Date.now() < 1000 * 60 * 30;
    }
    return true;
  }

  public isActive(): boolean {
    return this.hasStatus(InGamePhases);
  }

  public isPreGame(): boolean {
    return this.hasStatus(PreGamePhases);
  }

  public isCancelled(): boolean {
    return this.hasStatus([EventStatus.CANCELLED]);
  }

  public isAborted(): boolean {
    return this.hasStatus([EventStatus.ABORTED]);
  }

  public isCancelledOrAborted(): boolean {
    return this.hasStatus([EventStatus.CANCELLED, EventStatus.ABORTED]);
  }

  public isPostGame(): boolean {
    return this.hasStatus([EventStatus.POST_GAME]);
  }

  public isEnded(): boolean {
    return this.hasStatus([EventStatus.ENDED]);
  }

  public isPenalty(): boolean {
    return this.hasStatus([EventStatus.PENALTY_SHOOTOUT]);
  }

  public isOvertime(): boolean {
    return this.hasStatus([
      EventStatus.OT_FIRST_HALF,
      EventStatus.OT_FIRST_HALF_PAUSE,
      EventStatus.OT_SECOND_HALF,
    ]);
  }

  public isPostGameOrEnded(): boolean {
    return this.hasStatus([EventStatus.POST_GAME, EventStatus.ENDED]);
  }

  public isPostGameOrCancelled(): boolean {
    return this.hasStatus(PostGameAndCancelledPhases);
  }

  public isInAutoMode(): boolean {
    return this.hasStatus(AutoPhases);
  }

  public isHalftime(): boolean {
    return this.hasStatus([
      EventStatus.FIRST_HALF_PAUSE,
      EventStatus.SECOND_HALF_PAUSE,
      EventStatus.OT_FIRST_HALF_PAUSE,
    ]);
  }

  public hasStatus(statusList: EventStatus[]): boolean {
    if (!statusList) {
      return true;
    }
    return statusList.includes(this.status);
  }

  public isToday(): boolean {
    const start = new Date(this.timestamps.start).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    return start === today;
  }

  public isFuture(): boolean {
    const start = new Date(this.timestamps.start).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    return start > today;
  }

  public isPast(): boolean {
    const start = new Date(this.timestamps.start).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    return start < today;
  }

  private setHasReporter(): void {
    if (!this.teams) {
      return;
    }
    const home = this.teams.home;
    const away = this.teams.away;
    const reporters = home.moderators
      ?.concat(away.moderators)
      ?.filter((r) => !r.internal);
    this.hasReporter = reporters?.length > 0;
  }

  private setStreamInfo(): void {
    if (!this.streaming_info) {
      return;
    }
    const status = this.streaming_info.live_status;
    switch (status) {
      case LivestreamStatus.Upcoming:
        this.liveStreamStatusString = 'Stream: DEMNÄCHST';
        this.liveStreamStatusColor = '#0087d2';
        break;
      case LivestreamStatus.Cancelled:
        this.liveStreamStatusString = 'Stream: ABGESAGT';
        if (this.streaming_info.auto_cancel) {
          this.liveStreamStatusString = 'Stream: ABGESAGT (automatisch)';
        }
        this.liveStreamStatusColor = '#888888';
        break;
      case LivestreamStatus.Stopped:
        this.liveStreamStatusString = 'Stream: GESTOPPT';
        this.liveStreamStatusColor = '#888888';
        break;
      case LivestreamStatus.Live:
        this.liveStreamStatusString = 'Stream: LIVE';
        this.liveStreamStatusColor = '#f44336';
        break;
      case LivestreamStatus.Ended:
        this.liveStreamStatusString = 'Stream: BEENDET';
        this.liveStreamStatusColor = '#20791f';
        break;
      case LivestreamStatus.Replay:
        this.liveStreamStatusString = 'Stream: REPLAY';
        this.liveStreamStatusColor = '#517BBB';
        break;
      case LivestreamStatus.Highlights:
        this.liveStreamStatusString = 'Stream: HIGHLIGHTS';
        this.liveStreamStatusColor = '#9747FF';
        break;
    }
  }
}
